import {
  CREATE_CDV,
  DELETE_CDV,
  FETCH_CDV_BY_ID,
  FETCH_CDVS,
  SEARCH_CDVS,
  UPDATE_CDV,
} from "@apiUtils";
import {
  mutationFetch,
  type SearchPayload,
  type UseSearchQueryOptions,
  useSearchQuery,
} from "@atlas/sdk";
import type { IHubCdv } from "@models";
import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { AxiosError } from "axios";

export const HUB_CDV_KEY = "hubCdvKey";
export const HUB_CDV_BY_ID_KEY = "hubCdvByIdKey";

export const getHubCdvKey = () => [HUB_CDV_KEY] as const;

export const getHubCdvByIdKey = (cdvId: number) =>
  [HUB_CDV_BY_ID_KEY, cdvId] as const;

/** GET /cdvs may return a bare array, null, or a paged envelope depending on API version. */
function normalizeHubCdvListResponse(data: unknown): IHubCdv[] {
  if (Array.isArray(data)) {
    return data;
  }
  if (data != null && typeof data === "object") {
    const obj = data as Record<string, unknown>;
    if (Array.isArray(obj.items)) {
      return obj.items as IHubCdv[];
    }
    if (Array.isArray(obj.content)) {
      return obj.content as IHubCdv[];
    }
  }
  return [];
}

export const useHubCdv = (
  options?: Partial<
    UseQueryOptions<
      IHubCdv[],
      AxiosError,
      IHubCdv[],
      ReturnType<typeof getHubCdvKey>
    >
  >,
) => {
  return useQuery({
    queryKey: getHubCdvKey(),
    queryFn: async () => {
      const data = await mutationFetch<unknown>({
        url: FETCH_CDVS,
        method: "GET",
      });
      return normalizeHubCdvListResponse(data);
    },
    ...options,
  });
};

export const useHubCdvById = (
  cdvId: number,
  options?: Partial<
    UseQueryOptions<
      IHubCdv,
      AxiosError,
      IHubCdv,
      ReturnType<typeof getHubCdvByIdKey>
    >
  >,
) => {
  return useQuery({
    queryKey: getHubCdvByIdKey(cdvId),
    queryFn: async () => {
      return mutationFetch<IHubCdv>({
        url: FETCH_CDV_BY_ID.replace(":cdvId", String(cdvId)),
        method: "GET",
      });
    },
    ...options,
    enabled: (options?.enabled ?? true) && cdvId > 0,
  });
};

const createHubCdv = (body: IHubCdv): Promise<IHubCdv> => {
  return mutationFetch<IHubCdv>({
    url: CREATE_CDV,
    method: "POST",
    body,
  });
};

const updateHubCdv = (cdvId: number, body: IHubCdv): Promise<IHubCdv> => {
  return mutationFetch<IHubCdv>({
    url: UPDATE_CDV.replace(":cdvId", String(cdvId)),
    method: "PUT",
    body,
  });
};

const deleteHubCdv = (cdvId: number): Promise<void> => {
  return mutationFetch<void>({
    url: DELETE_CDV.replace(":cdvId", String(cdvId)),
    method: "DELETE",
  });
};

export const useCreateHubCdv = (
  options?: UseMutationOptions<IHubCdv, AxiosError, IHubCdv>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createHubCdv,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: getHubCdvKey() });
      queryClient.invalidateQueries({ queryKey: [SEARCH_CDVS] });
      if (data.id != null) {
        queryClient.invalidateQueries({
          queryKey: getHubCdvByIdKey(data.id),
        });
      }
    },
    ...options,
  });
};

export const useUpdateHubCdv = (
  options?: UseMutationOptions<IHubCdv, AxiosError, IHubCdv>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: IHubCdv) => {
      const cdvId = body.id;
      if (cdvId == null) {
        throw new Error("id is required for update");
      }
      return updateHubCdv(cdvId, body);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: getHubCdvKey() });
      queryClient.invalidateQueries({ queryKey: [SEARCH_CDVS] });
      if (data.id != null) {
        queryClient.invalidateQueries({
          queryKey: getHubCdvByIdKey(data.id),
        });
      }
    },
    ...options,
  });
};

export const useDeleteHubCdv = (
  options?: UseMutationOptions<void, AxiosError, number>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteHubCdv,
    onSuccess: (_, cdvId) => {
      queryClient.invalidateQueries({ queryKey: getHubCdvKey() });
      queryClient.invalidateQueries({ queryKey: [SEARCH_CDVS] });
      queryClient.invalidateQueries({
        queryKey: getHubCdvByIdKey(cdvId),
      });
    },
    ...options,
  });
};

export const useSearchHubCdvs = (
  payload: SearchPayload,
  options?: Omit<UseSearchQueryOptions<IHubCdv>, "url" | "payload">,
) => {
  return useSearchQuery<IHubCdv>({
    url: SEARCH_CDVS,
    payload,
    ...options,
  });
};
